import React, { useEffect, useMemo, useState } from 'react';
import {
  Award, DollarSign, Clock, CheckCircle,
  GraduationCap, Settings, Zap, TrendingUp, Building,
  Calendar, Target, Globe, Info, AlertTriangle, ShieldCheck, FileSearch,
} from 'lucide-react';
import LoadingSpinner from '../UI/LoadingSpinner';
import AccordionSection from '../UI/AccordionSection';
import { StatusBadge } from '../UI/StatusBadge';
import { CountBadge } from '../UI/CountBadge';
import { InfoBadge } from '../UI/InfoBadge';
import { BcaSchemeBadge } from '../UI/BcaSchemeBadge';
import ClassificationSetupModal from './ClassificationSetupModal';
import { useEmployeeCertificates } from '../../hooks/useTimelineData';
import {
  useConstructionExperienceSummary,
  useWorkerClassification,
  useUpgradePathways,
  useMissingCertifications,
  useWorkerActiveSalary,
  useWorkerR1Registrations,
  useWorkerConstructionStatus,
  useCertificateEvidence,
} from '../../hooks/useSkillsUpgradingData';
import { formatDate, formatDaySpan } from '../../utils/dateUtils';

const PATHWAY_ICONS = {
  coretrade: GraduationCap,
  multi_skilling: Settings,
  direct_r1: Zap,
  mbf: TrendingUp,
};

const PATHWAY_LABELS = {
  coretrade: 'CoreTrade',
  multi_skilling: 'Multi-Skilling',
  direct_r1: 'Direct R1',
  mbf: 'MBF',
};

const REGISTRATION_AVATAR = {
  expired: 'bg-red-100 text-red-600',
  expiring_soon: 'bg-orange-100 text-orange-600',
};
const DEFAULT_AVATAR = 'bg-blue-100 text-blue-600';

const StatTile = ({ icon: Icon, color, value, label, sub }) => (
  <div className="flex items-start gap-2.5 rounded-xl border border-gray-200 p-3">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-gray-900 capitalize leading-tight">{value}</p>
      <p className="text-xs text-gray-500 leading-tight mt-0.5">{label}</p>
      {sub && <p className="text-[11px] text-gray-400 leading-tight mt-0.5 truncate">{sub}</p>}
    </div>
  </div>
);

const pathwayHasSkillsEvidence = (pathwayName, matchedCertificates, registrations) => {
  const matched = (matchedCertificates || []).some((match) => {
    if (match.pathway !== pathwayName) return false;
    if (pathwayName === 'coretrade') {
      return match.certification_type === 'coretrade_assessment';
    }
    if (pathwayName === 'multi_skilling') {
      return ['sec_k_basic', 'sec_k_higher', 'spm', 'wsq_advanced', 'multi_skilling'].includes(
        match.certification_type,
      );
    }
    return true;
  });
  // Registration on the pathway itself proves skills were already accepted by BCA.
  const registered = (registrations || []).some((reg) => reg.pathway === pathwayName);
  return matched || registered;
};

/**
 * Shared pathway gate evaluation for summary + detail checklist.
 */
const evaluatePathway = ({
  pathway,
  experienceYears,
  experienceDisplay,
  basicSalary,
  hasClassification,
  missingByPathway,
  matchedCertificates,
  registrations,
}) => {
  const pathwayLabel = pathway.pathway_name_display || pathway.pathway_name?.replace(/_/g, ' ');
  const expRequired = pathway.minimum_years_experience || 0;
  const expMax = pathway.maximum_years_experience;
  const expPass = experienceYears >= expRequired && (expMax == null || experienceYears <= expMax);

  const salaryRequired = pathway.minimum_fixed_monthly_salary
    ? parseFloat(pathway.minimum_fixed_monthly_salary)
    : 0;
  const salaryPass = salaryRequired <= 0 || (basicSalary !== null && basicSalary >= salaryRequired);
  const salaryUnknown = salaryRequired > 0 && basicSalary === null;

  const certsRequired = pathway.skills_assessment_required || pathway.specific_certification_required;
  const certsConfigured = (pathway.certification_requirements_count || 0) > 0;
  const pathwayMissing = missingByPathway[pathway.pathway_name]?.missing_certifications || [];
  const liveSkillsEvidence = pathwayHasSkillsEvidence(
    pathway.pathway_name,
    matchedCertificates,
    registrations,
  );

  let certsUnknown = false;
  let certsPass = !certsRequired;
  let skillsDetail = 'Not required';
  let syntheticMissing = [];

  if (certsRequired) {
    if (!hasClassification) {
      if (liveSkillsEvidence) {
        certsUnknown = true;
        certsPass = false;
        skillsDetail = pathway.pathway_name === 'coretrade'
          ? 'Assessment evidence on file — classification required to confirm'
          : 'Evidence on file — classification required to confirm';
      } else if (pathway.pathway_name === 'coretrade') {
        certsPass = false;
        skillsDetail = 'No CoreTrade assessment on file — CET is for renewal only';
        syntheticMissing = [{
          certification_name: 'CoreTrade Skills Assessment',
          certification_short_name: 'CoreTrade',
        }];
      } else if (pathway.pathway_name === 'multi_skilling') {
        certsPass = false;
        skillsDetail = 'No Multi-Skilling trade evidence on file';
        syntheticMissing = [{
          certification_name: 'Multi-Skilling trade / registration evidence',
          certification_short_name: 'Multi-Skilling',
        }];
      } else {
        certsUnknown = true;
        certsPass = false;
        skillsDetail = 'Classification required to assess';
      }
    } else if (!certsConfigured) {
      certsUnknown = true;
      certsPass = false;
      skillsDetail = 'Required -- requirement list not configured';
    } else {
      const missingEmpty = pathwayMissing.length === 0;
      if (pathway.pathway_name === 'coretrade') {
        // Never green CoreTrade on empty missing alone (e.g. WSH/CSOC-only).
        certsPass = missingEmpty && liveSkillsEvidence;
        skillsDetail = certsPass
          ? 'CoreTrade assessment evidence met'
          : liveSkillsEvidence
            ? `${pathwayMissing.length} certification${pathwayMissing.length !== 1 ? 's' : ''} missing`
            : 'No CoreTrade assessment on file — CET is for renewal only';
        if (!certsPass && !liveSkillsEvidence && pathwayMissing.length === 0) {
          syntheticMissing = [{
            certification_name: 'CoreTrade Skills Assessment',
            certification_short_name: 'CoreTrade',
          }];
        }
      } else if (pathway.pathway_name === 'multi_skilling') {
        const altRoutes = pathwayMissing.filter((row) => row.is_alternative_route);
        certsPass = missingEmpty;
        if (certsPass) {
          skillsDetail = 'Multi-Skilling evidence met';
        } else if (altRoutes.length >= 2) {
          // SEC(K) already on file — remaining items are route choices, not dual deficits.
          skillsDetail = 'Choose a route: 2nd SEC(K) trade or WSQ Advanced WSH';
        } else {
          skillsDetail = `${pathwayMissing.length} item${pathwayMissing.length !== 1 ? 's' : ''} still needed`;
        }
      } else {
        certsPass = missingEmpty;
        skillsDetail = certsPass
          ? 'All certifications met'
          : `${pathwayMissing.length} certification${pathwayMissing.length !== 1 ? 's' : ''} missing`;
      }
    }
  }

  const displayMissing = pathwayMissing.length > 0 ? pathwayMissing : syntheticMissing;
  const hasUnknown = salaryUnknown || certsUnknown;
  const allPass = expPass && salaryPass && certsPass && !hasUnknown;
  const failed = !expPass || (!salaryUnknown && !salaryPass) || (!certsUnknown && !certsPass);
  const monthsShort = expPass || experienceYears >= expRequired
    ? 0
    : Math.ceil((expRequired - experienceYears) * 12);

  let primaryBlocker = null;
  if (!expPass && monthsShort > 0) {
    primaryBlocker = `Need ${monthsShort} more mth${monthsShort !== 1 ? 's' : ''} experience`;
  } else if (!salaryUnknown && !salaryPass) {
    primaryBlocker = `Salary below $${salaryRequired.toLocaleString()}`;
  } else if (certsUnknown) {
    primaryBlocker = skillsDetail;
  } else if (!certsPass) {
    primaryBlocker = skillsDetail;
  }

  return {
    pathway,
    pathwayLabel,
    IconComponent: PATHWAY_ICONS[pathway.pathway_name] || Award,
    expRequired,
    expMax,
    expPass,
    salaryRequired,
    salaryPass,
    salaryUnknown,
    certsRequired,
    certsPass,
    certsUnknown,
    skillsDetail,
    displayMissing,
    hasUnknown,
    allPass,
    failed,
    monthsShort,
    primaryBlocker,
    experienceDisplay,
    experienceYears,
    basicSalary,
  };
};

const formatRegDate = (iso) => {
  if (!iso) return '\u2014';
  const formatted = formatDate(iso);
  return formatted === 'N/A' || formatted === 'Invalid Date' ? iso : formatted;
};

/**
 * Skills Upgrading tab — eligibility intelligence for construction workers.
 *
 * First viewport: next action + status + pathway decision.
 * Detail (registrations, evidence, checklists, missing certs) is progressive.
 *
 * @param {object} profile - Staff profile (needs staff_id)
 * @param {function} [onReviewCertificates] - HR-only: jump to Documents verification
 */
const SkillsUpgradingTab = ({ profile, onReviewCertificates }) => {
  const staffId = profile?.staff_id;

  const { data: experienceSummary, isLoading: experienceLoading } = useConstructionExperienceSummary(staffId);
  const { data: constructionData, isLoading: classificationLoading } = useWorkerClassification(staffId);
  const { data: upgradePathways = [], isLoading: pathwaysLoading } = useUpgradePathways();
  const { data: missingCertsData } = useMissingCertifications(constructionData?.id);
  const { data: workerSalary } = useWorkerActiveSalary(staffId);
  const { data: registrationsData } = useWorkerR1Registrations(staffId);
  const { data: constructionStatus } = useWorkerConstructionStatus(staffId);
  const { data: certificateEvidence } = useCertificateEvidence(staffId);
  const { data: certificates = [], isLoading: certificatesLoading } = useEmployeeCertificates(staffId);

  const loading = experienceLoading || classificationLoading || pathwaysLoading;

  const verifiedCertificates = useMemo(
    () => (certificates || []).filter(
      (cert) => (cert.cert_verification_status || '').toLowerCase() === 'verified',
    ),
    [certificates],
  );

  const levyRelevantCertificates = useMemo(
    () => verifiedCertificates.filter((cert) => cert.bca_scheme),
    [verifiedCertificates],
  );

  const registrations = registrationsData?.registrations || [];
  const urgentRegistration = registrations.find((reg) => reg.validity === 'expired')
    || registrations.find((reg) => reg.validity === 'expiring_soon');
  const hasUrgentRegistration = Boolean(urgentRegistration);

  const [openSections, setOpenSections] = useState({
    registrations: false,
    evidence: false,
    pathways: false,
    missing: false,
  });

  useEffect(() => {
    setOpenSections({
      registrations: false,
      evidence: false,
      pathways: false,
      missing: false,
    });
  }, [staffId]);

  useEffect(() => {
    if (hasUrgentRegistration) {
      setOpenSections((prev) => ({ ...prev, registrations: true }));
    }
  }, [staffId, hasUrgentRegistration]);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // HR-only: classification setup is a judgement call (skill level, source
  // country, levy) that self-service workers must never make for themselves.
  // `onReviewCertificates` is only ever passed from the HR employee-management
  // view, never from the self-service Profile page — reuse it as the HR gate.
  const canManageClassification = Boolean(onReviewCertificates);
  const [showClassificationSetup, setShowClassificationSetup] = useState(false);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!constructionData && !experienceSummary?.record_count && !constructionStatus?.source_country && !constructionStatus?.actual_monthly_levy && !constructionStatus?.monthly_levy_rate) {
    return (
      <div className="space-y-4">
        <div className="card">
          <div className="card-content">
            <div className="text-center py-12">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Building className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Classification Record
              </h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto mb-4">
                This worker has no construction worker classification and no WPOL employment history on file.
                Pathway eligibility cannot be assessed until a classification record exists.
              </p>
              <div className="flex items-center justify-center gap-2 mb-4">
                <StatusBadge status="required" label="Classification setup required" />
              </div>
              <div className="flex items-center justify-center gap-2">
                {canManageClassification && (
                  <button
                    type="button"
                    onClick={() => setShowClassificationSetup(true)}
                    className="btn btn-sm btn-admin-primary inline-flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Set up classification
                  </button>
                )}
                {onReviewCertificates && (
                  <button
                    type="button"
                    onClick={onReviewCertificates}
                    className="btn btn-sm btn-admin-outline inline-flex items-center gap-2"
                  >
                    <FileSearch className="w-4 h-4" />
                    Review certificates
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {!certificatesLoading && levyRelevantCertificates.length > 0 && (
          <AccordionSection
            expanded={openSections.evidence}
            onToggle={() => toggleSection('evidence')}
            theme="admin"
            contentVariant="padded"
            icon={<Award className="w-4 h-4 text-blue-600" />}
            iconBg="bg-blue-100"
            title="Verified qualification evidence"
            trailing={<CountBadge count={levyRelevantCertificates.length} theme="admin" size="md" />}
          >
            <VerifiedEvidenceList
              certificates={levyRelevantCertificates}
              matchedCertificates={certificateEvidence?.matched_certificates || []}
              cetEvidence={certificateEvidence?.cet_renewal_evidence || []}
            />
          </AccordionSection>
        )}
        {canManageClassification && (
          <ClassificationSetupModal
            isOpen={showClassificationSetup}
            onClose={() => setShowClassificationSetup(false)}
            profile={profile}
            constructionStatus={constructionStatus}
            experienceSummary={experienceSummary}
          />
        )}
      </div>
    );
  }

  const experienceYears = experienceSummary?.construction_experience_years
    ?? (constructionData?.total_construction_experience_years != null
      ? parseFloat(constructionData.total_construction_experience_years)
      : 0);
  const experienceDays = experienceSummary?.construction_experience_days ?? 0;
  const experienceDisplay = experienceSummary?.construction_experience_display;

  const missingByPathway = missingCertsData?.missing_certifications_by_pathway || {};
  const allMissingCerts = missingCertsData?.missing_certifications || [];
  const hasClassification = Boolean(constructionData?.id);
  const matchedCertificates = certificateEvidence?.matched_certificates || [];
  const cetEvidence = certificateEvidence?.cet_renewal_evidence || [];
  const hasCertificateEvidence = Boolean(certificateEvidence?.has_certificate_evidence);

  const sourceCountry = constructionStatus?.source_country || constructionData?.source_country;
  const basicSalary = workerSalary?.payroll_basic_salary
    ? parseFloat(workerSalary.payroll_basic_salary)
    : null;

  const applicablePathways = upgradePathways.filter((pathway) => {
    const countries = pathway.applicable_source_countries || [];
    if (countries.length === 0 || !sourceCountry) return true;
    return countries.includes(sourceCountry);
  });

  const pathwayEvals = applicablePathways.map((pathway) => evaluatePathway({
    pathway,
    experienceYears,
    experienceDisplay,
    basicSalary,
    hasClassification,
    missingByPathway,
    matchedCertificates,
    registrations,
  }));

  const eligiblePathways = pathwayEvals.filter((row) => row.allPass);
  const reviewPathways = pathwayEvals.filter((row) => !row.allPass && !row.failed);
  const blockedPathways = pathwayEvals.filter((row) => row.failed);
  const registeredPathwayNames = new Set(registrations.map((reg) => reg.pathway));
  const eligibleNotRegistered = eligiblePathways.filter(
    (row) => !registeredPathwayNames.has(row.pathway.pathway_name),
  );

  return (
    <div className="space-y-4">
      <ClassificationStatus
        constructionData={constructionData}
        constructionStatus={constructionStatus}
        experienceDisplay={experienceDisplay}
        experienceYears={experienceYears}
        experienceDays={experienceDays}
        experienceSummary={experienceSummary}
      />

      <LevyActionCard
        urgentRegistration={urgentRegistration}
        hasClassification={hasClassification}
        hasCertificateEvidence={hasCertificateEvidence}
        suggestedPathways={certificateEvidence?.suggested_pathways || []}
        cetEvidence={cetEvidence}
        onReviewCertificates={onReviewCertificates}
        onSetupClassification={canManageClassification ? () => setShowClassificationSetup(true) : null}
        eligiblePathways={eligiblePathways}
        eligibleNotRegistered={eligibleNotRegistered}
        reviewPathways={reviewPathways}
        blockedPathways={blockedPathways}
        constructionData={constructionData}
        onExpandPathways={() => setOpenSections((prev) => ({ ...prev, pathways: true }))}
      />

      {registrations.length > 0 && (
        <AccordionSection
          expanded={openSections.registrations}
          onToggle={() => toggleSection('registrations')}
          theme="admin"
          contentVariant="padded"
          icon={<ShieldCheck className="w-4 h-4 text-blue-600" />}
          iconBg="bg-blue-100"
          title="Current BCA registrations"
          trailing={(
            <>
              <CountBadge count={registrations.length} theme="admin" size="md" />
              {hasUrgentRegistration && (
                <StatusBadge
                  status={registrations.some((r) => r.validity === 'expired') ? 'expired' : 'expiring_soon'}
                />
              )}
            </>
          )}
        >
          <RegistrationsList
            registrations={registrations}
            cetEvidence={cetEvidence}
            headlineRegistration={urgentRegistration}
          />
        </AccordionSection>
      )}

      {!certificatesLoading && (levyRelevantCertificates.length > 0 || cetEvidence.length > 0) && (
        <AccordionSection
          expanded={openSections.evidence}
          onToggle={() => toggleSection('evidence')}
          theme="admin"
          contentVariant="padded"
          icon={<Award className="w-4 h-4 text-blue-600" />}
          iconBg="bg-blue-100"
          title="Verified qualification evidence"
          trailing={(
            <CountBadge
              count={levyRelevantCertificates.length || cetEvidence.length}
              theme="admin"
              size="md"
            />
          )}
        >
          <VerifiedEvidenceList
            certificates={levyRelevantCertificates}
            matchedCertificates={matchedCertificates}
            cetEvidence={cetEvidence}
          />
        </AccordionSection>
      )}

      {pathwayEvals.length > 0 && (
        <AccordionSection
          expanded={openSections.pathways}
          onToggle={() => toggleSection('pathways')}
          theme="admin"
          contentVariant="padded"
          icon={<Target className="w-4 h-4 text-amber-600" />}
          iconBg="bg-amber-100"
          title="Pathway checklists"
          trailing={<CountBadge count={pathwayEvals.length} theme="admin" size="md" />}
        >
          <PathwayChecklistList
            pathwayEvals={pathwayEvals}
            constructionData={constructionData}
            matchedCertificates={matchedCertificates}
            registrations={registrations}
          />
        </AccordionSection>
      )}

      {hasClassification && allMissingCerts.length > 0 && (
        <AccordionSection
          expanded={openSections.missing}
          onToggle={() => toggleSection('missing')}
          theme="admin"
          contentVariant="padded"
          icon={<AlertTriangle className="w-4 h-4 text-amber-600" />}
          iconBg="bg-amber-100"
          title="Missing certifications"
          trailing={<CountBadge count={allMissingCerts.length} theme="warning" size="md" />}
        >
          <p className="text-xs text-gray-500 mb-3">
            Grouped by pathway — items under different pathways are separate eligibility checks, not a single combined deficit.
          </p>
          <MissingCertsList missingCerts={allMissingCerts} />
        </AccordionSection>
      )}

      {canManageClassification && (
        <ClassificationSetupModal
          isOpen={showClassificationSetup}
          onClose={() => setShowClassificationSetup(false)}
          profile={profile}
          constructionStatus={constructionStatus}
          experienceSummary={experienceSummary}
        />
      )}
    </div>
  );
};


/**
 * Single decision card for the tab — merges the old "next action" strip and
 * "pathway decision" summary so HR sees one coherent story instead of two
 * cards that could disagree (e.g. an expired registration flagged as both
 * "renew via CET" and, two inches down, "ELIGIBLE — save $400/mth").
 *
 * Priority ladder: expired registration > expiring-soon registration >
 * classification setup required > eligible-not-registered upgrade
 * opportunity > certificate evidence pending HR review > all clear.
 */
const LevyActionCard = ({
  urgentRegistration,
  hasClassification,
  hasCertificateEvidence,
  suggestedPathways,
  cetEvidence,
  onReviewCertificates,
  onSetupClassification,
  eligiblePathways,
  eligibleNotRegistered,
  reviewPathways,
  blockedPathways,
  constructionData,
  onExpandPathways,
}) => {
  const totalPathways = eligiblePathways.length + reviewPathways.length + blockedPathways.length;
  const savings = constructionData?.potential_monthly_levy_savings;
  const annualSavings = constructionData?.annual_levy_savings;

  let tone = 'neutral';
  let icon = <Info className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />;
  let title = 'No urgent action';
  let detail = 'Review pathway checklists below when needed.';
  let status = null;

  if (urgentRegistration?.validity === 'expired') {
    tone = 'danger';
    icon = <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />;
    title = `${urgentRegistration.pathway_label || 'BCA registration'} lapsed`;
    const days = urgentRegistration.days_until_expiry;
    const agoText = typeof days === 'number'
      ? `Expired ${formatDaySpan(Math.abs(days), 'long')} ago`
      : 'Expired';
    detail = savings > 0
      ? `${agoText} — renew via CET in the registered trade to restore the Higher-Skilled levy and recover $${savings}/mth${annualSavings > 0 ? ` ($${annualSavings}/yr)` : ''}.`
      : `${agoText} — renew via CET in the registered trade.`;
    status = <StatusBadge status="expired" />;
  } else if (urgentRegistration?.validity === 'expiring_soon') {
    tone = 'warning';
    icon = <Clock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />;
    title = `${urgentRegistration.pathway_label || 'BCA registration'} renews soon`;
    const days = urgentRegistration.days_until_expiry;
    detail = typeof days === 'number'
      ? `Expires in ${formatDaySpan(days, 'long')} — renew via CET${urgentRegistration.registered_trade ? ` (${urgentRegistration.registered_trade})` : ''}.`
      : 'Renew via CET in the registered trade category.';
    if (cetEvidence?.length > 0) {
      detail += ` ${cetEvidence.length} CET certificate${cetEvidence.length !== 1 ? 's' : ''} already on file.`;
    }
    status = <StatusBadge status="expiring_soon" />;
  } else if (!hasClassification) {
    tone = 'warning';
    icon = <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />;
    title = 'Classification setup required';
    detail = hasCertificateEvidence
      ? 'Certificate evidence is on file, but eligibility needs a construction worker classification record.'
      : 'Pathway eligibility checks need a construction worker classification record.';
    status = <StatusBadge status="required" label="Setup required" />;
  } else if (eligibleNotRegistered.length > 0) {
    const headline = eligibleNotRegistered[0];
    tone = 'info';
    icon = <TrendingUp className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />;
    title = `${headline.pathwayLabel} — upgrade opportunity`;
    detail = savings > 0
      ? `Meets experience, salary and skills gates — potential save $${savings}/mth${annualSavings > 0 ? ` ($${annualSavings}/yr)` : ''}.`
      : 'Meets experience, salary and skills gates.';
    status = <StatusBadge status="eligible" label="ELIGIBLE" />;
  } else if (hasCertificateEvidence && suggestedPathways?.length > 0) {
    const labels = suggestedPathways
      .map((row) => row.pathway_label || PATHWAY_LABELS[row.pathway] || row.pathway)
      .filter(Boolean);
    tone = 'info';
    icon = <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />;
    title = 'Certificate evidence ready for HR review';
    detail = labels.length > 0
      ? `Matched evidence suggests ${labels.join(', ')}. Official BCA/MOM registration remains the source of truth.`
      : 'Matched certificates are review-only — official BCA/MOM registration remains the source of truth.';
    status = <StatusBadge status="pending_review" label="HR review" />;
  }

  const toneClasses = {
    danger: 'border-red-200 bg-red-50',
    warning: 'border-amber-200 bg-amber-50',
    info: 'border-blue-200 bg-blue-50',
    neutral: 'border-gray-200 bg-white',
  }[tone];

  const titleColor = {
    danger: 'text-red-900',
    warning: 'text-amber-900',
    info: 'text-blue-900',
    neutral: 'text-gray-900',
  }[tone];

  const detailColor = {
    danger: 'text-red-800',
    warning: 'text-amber-800',
    info: 'text-blue-800',
    neutral: 'text-gray-600',
  }[tone];

  return (
    <div className={`rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-start gap-3 ${toneClasses}`}>
      <div className="flex items-start gap-3 min-w-0 flex-1">
        {icon}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <p className={`text-sm font-semibold ${titleColor}`}>{title}</p>
            {status}
          </div>
          <p className={`text-xs ${detailColor}`}>{detail}</p>
          {totalPathways > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {eligibleNotRegistered.length > 0 && (
                <InfoBadge color="green" label={`${eligibleNotRegistered.length} eligible`} size="xs" />
              )}
              {reviewPathways.length > 0 && (
                <InfoBadge color="amber" label={`${reviewPathways.length} review`} size="xs" />
              )}
              {blockedPathways.length > 0 && (
                <InfoBadge color="gray" label={`${blockedPathways.length} not eligible`} size="xs" />
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 flex-shrink-0 self-start sm:self-auto">
        <div className="flex items-center gap-2">
          {!hasClassification && onSetupClassification && (
            <button
              type="button"
              onClick={onSetupClassification}
              className="btn btn-sm btn-admin-primary inline-flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Set up classification
            </button>
          )}
          {onReviewCertificates && (
            <button
              type="button"
              onClick={onReviewCertificates}
              className="btn btn-sm btn-admin-outline inline-flex items-center gap-2"
            >
              <FileSearch className="w-4 h-4" />
              Review certificates
            </button>
          )}
        </div>
        {totalPathways > 0 && onExpandPathways && (
          <button
            type="button"
            onClick={onExpandPathways}
            className="text-xs font-medium text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded"
          >
            View checklists
          </button>
        )}
      </div>
    </div>
  );
};


const ClassificationStatus = ({ constructionData, constructionStatus, experienceDisplay, experienceYears, experienceDays, experienceSummary }) => {
  const skillRaw = constructionData?.current_skill_level || constructionStatus?.skill_level;
  const skillDisplay = constructionData?.current_skill_level_display
    || (skillRaw ? skillRaw.replace(/_/g, ' ') : 'Unclassified');

  const levyRate = constructionStatus?.monthly_levy_rate != null
    ? parseFloat(constructionStatus.monthly_levy_rate)
    : null;
  const billedLevy = constructionStatus?.actual_monthly_levy != null
    ? parseFloat(constructionStatus.actual_monthly_levy)
    : null;
  const levyDisplay = levyRate != null ? levyRate : billedLevy;

  const sourceCountry = constructionStatus?.source_country;
  const sourceCategory = constructionStatus?.source_country_category_display;

  const experienceSub = experienceDays > 0
    ? `${experienceDays} days · ${experienceSummary?.record_count || 0} employer${(experienceSummary?.record_count || 0) !== 1 ? 's' : ''}`
    : null;

  const levySourceLabel = {
    mom_bill: 'MOM bill',
    payroll: 'payroll est.',
    configured_rate: 'published rate',
  }[constructionStatus?.levy_source];

  const levySub = [
    levySourceLabel,
    constructionStatus?.levy_period,
  ].filter(Boolean).join(' · ') || null;

  return (
    <div className="card">
      <div className="card-header card-header-sm">
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Building className="w-5 h-5 text-blue-600" />
          Worker status
        </h3>
      </div>
      <div className="card-content card-content-sm">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile
            icon={Award}
            color="bg-blue-100 text-blue-600"
            value={skillDisplay}
            label="Skill Level"
          />
          <StatTile
            icon={Calendar}
            color="bg-green-100 text-green-600"
            value={experienceDisplay ?? `${experienceYears} yrs`}
            label="Construction Experience"
            sub={experienceSub}
          />
          {levyDisplay != null && (
            <StatTile
              icon={DollarSign}
              color="bg-orange-100 text-orange-600"
              value={`$${levyDisplay.toLocaleString()}`}
              label="Monthly Levy"
              sub={levySub}
            />
          )}
          {sourceCountry && (
            <StatTile
              icon={Globe}
              color="bg-blue-100 text-blue-600"
              value={sourceCountry}
              label={sourceCategory}
            />
          )}
        </div>

        {!constructionData && (
          <p className="text-xs text-gray-500 mt-3">
            No upgrade classification record yet &mdash; values above are derived live from the worker&rsquo;s nationality, payroll levy and employment history.
          </p>
        )}
      </div>
    </div>
  );
};


const RegistrationsList = ({ registrations, cetEvidence = [], headlineRegistration }) => (
  <div className="space-y-3">
    {registrations.map((reg, index) => {
      const IconComponent = PATHWAY_ICONS[reg.pathway] || Award;
      const days = reg.days_until_expiry;
      const avatarColor = REGISTRATION_AVATAR[reg.validity] || DEFAULT_AVATAR;
      // The headline registration's expiry story is already the LevyActionCard
      // headline above — repeating it here is the exact duplication we're removing.
      const isHeadline = headlineRegistration?.pathway === reg.pathway;
      const showCetHint = (
        (reg.validity === 'expiring_soon' || reg.validity === 'expired')
        && cetEvidence.length > 0
      );

      return (
        <div
          key={`${reg.pathway}-${reg.reference || index}`}
          className={`rounded-xl border p-3 ${reg.validity === 'expired' ? 'border-red-200 bg-red-50/40' : reg.validity === 'expiring_soon' ? 'border-orange-200 bg-orange-50/40' : 'border-gray-200 bg-white'}`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${avatarColor}`}>
                <IconComponent className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-semibold text-gray-900 truncate">{reg.pathway_label}</h4>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {[reg.registered_trade, formatRegDate(reg.expiry_date), reg.reference]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <StatusBadge status={reg.validity} />
            </div>
          </div>

          {!isHeadline && reg.validity === 'expired' && (
            <p className="mt-2 text-xs text-red-700">
              Expired{typeof days === 'number' ? ` ${formatDaySpan(Math.abs(days), 'long')} ago` : ''} — renew via CET.
            </p>
          )}
          {!isHeadline && reg.validity === 'expiring_soon' && (
            <p className="mt-2 text-xs text-orange-700">
              Expires in {formatDaySpan(days, 'long')} — renew via CET in the registered trade.
            </p>
          )}
          {showCetHint && (
            <p className="mt-1 text-xs text-blue-800">
              {cetEvidence.length} verified CET certificate{cetEvidence.length !== 1 ? 's' : ''} on file.
            </p>
          )}
        </div>
      );
    })}
  </div>
);


const VerifiedEvidenceList = ({ certificates, matchedCertificates, cetEvidence }) => {
  const matchedByCertId = useMemo(() => {
    const map = new Map();
    (matchedCertificates || []).forEach((match) => {
      const key = String(match.certificate_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(match);
    });
    return map;
  }, [matchedCertificates]);

  const cetIds = useMemo(
    () => new Set((cetEvidence || []).map((row) => String(row.certificate_id))),
    [cetEvidence],
  );

  const showEmpty = (!certificates || certificates.length === 0)
    && (!cetEvidence || cetEvidence.length === 0);

  if (showEmpty) {
    return (
      <div className="text-center py-6 rounded-xl border border-dashed border-gray-200 bg-gray-50">
        <Award className="w-7 h-7 mx-auto text-gray-400 mb-2" />
        <p className="text-sm font-medium text-gray-900">No levy-relevant certificates yet</p>
        <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
          Verified SEC(K), CoreTrade, CET, ALP, SPM or WSQ Advanced WSH certificates will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        Scheme tags are review-only. Official BCA/MOM registration remains the source of truth.
      </p>
      {certificates.map((cert) => {
        const matches = matchedByCertId.get(String(cert.id)) || [];
        const pathwayHint = [...new Set(
          matches.map((m) => m.pathway_label || PATHWAY_LABELS[m.pathway]).filter(Boolean),
        )].join(', ');
        const isCet = cetIds.has(String(cert.id)) || cert.bca_scheme?.code === 'cet';

        return (
          <div
            key={cert.id}
            className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 bg-white"
          >
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Award className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-0.5">
                <h4 className="text-sm font-semibold text-gray-900 truncate">
                  {cert.cert_name}
                </h4>
                {cert.bca_scheme && <BcaSchemeBadge scheme={cert.bca_scheme} />}
                <StatusBadge status="verified" />
              </div>
              <p className="text-xs text-gray-500 truncate">
                {[
                  cert.cert_issuing_organization,
                  cert.cert_issue_date ? `Issued ${formatDate(cert.cert_issue_date)}` : null,
                  cert.cert_is_lifetime
                    ? 'Lifetime'
                    : (cert.cert_expiry_date ? `Expires ${formatDate(cert.cert_expiry_date)}` : null),
                  isCet ? 'CET renewal' : null,
                  pathwayHint || null,
                ].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};


const PathwayChecklistList = ({ pathwayEvals, constructionData, matchedCertificates = [], registrations = [] }) => (
  <div className="space-y-3">
    {pathwayEvals.map((row) => {
      const IconComponent = row.IconComponent;
      const avatarColor = row.allPass ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500';
      const onFile = (matchedCertificates || []).filter(
        (match) => match.pathway === row.pathway.pathway_name,
      );
      // Reconcile with Current BCA registrations instead of contradicting it —
      // an eligible pathway the worker already holds (even lapsed) is not a
      // fresh "ELIGIBLE, save $X" opportunity, it's a renewal.
      const registration = registrations.find((reg) => reg.pathway === row.pathway.pathway_name);
      const isRegisteredActive = row.allPass && registration?.validity === 'active';
      const isRegisteredLapsed = row.allPass
        && (registration?.validity === 'expired' || registration?.validity === 'expiring_soon');
      const badgeStatus = isRegisteredActive
        ? 'completed'
        : isRegisteredLapsed
          ? registration.validity
          : row.allPass ? 'eligible' : row.failed ? 'not_eligible' : 'pending';
      const badgeLabel = isRegisteredActive
        ? 'REGISTERED'
        : isRegisteredLapsed
          ? 'LAPSED — RENEW'
          : row.allPass ? 'ELIGIBLE' : row.failed ? 'NOT ELIGIBLE' : 'REVIEW NEEDED';
      // Dedupe by certification_type + certificate name for compact chips.
      const onFileChips = [];
      const seenOnFile = new Set();
      onFile.forEach((match) => {
        const label = match.requirement_short_name
          || match.certificate_short_name
          || match.certification_type
          || 'Evidence';
        const key = `${match.certification_type}:${match.certificate_id || label}`;
        if (seenOnFile.has(key)) return;
        seenOnFile.add(key);
        onFileChips.push({
          key,
          label,
          title: match.certificate_name || label,
        });
      });
      const stillNeeded = row.displayMissing || [];
      const showAltHint = stillNeeded.filter((c) => c.is_alternative_route).length >= 2;

      return (
        <div
          key={row.pathway.id}
          className={`rounded-xl border p-3 ${row.allPass ? 'border-green-200 bg-green-50/50' : 'border-gray-200 bg-white'}`}
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${avatarColor}`}>
                <IconComponent className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <h4
                  className="text-sm font-semibold text-gray-900 truncate"
                  title={row.pathway.pathway_description || undefined}
                >
                  {row.pathwayLabel}
                </h4>
              </div>
            </div>
            <div className="flex-shrink-0">
              <StatusBadge status={badgeStatus} label={badgeLabel} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
            <CriteriaItem
              pass={row.expPass}
              label="Experience"
              detail={
                row.expPass
                  ? `${row.experienceDisplay ?? `${row.experienceYears} yrs`} (min ${row.expRequired} yrs)`
                  : row.monthsShort > 0
                    ? `${row.experienceDisplay ?? `${row.experienceYears} yrs`} -- need ${row.monthsShort} more mth${row.monthsShort !== 1 ? 's' : ''}`
                    : `${row.experienceDisplay ?? `${row.experienceYears} yrs`} -- exceeds max ${row.expMax} yrs`
              }
            />
            <CriteriaItem
              pass={row.salaryUnknown ? null : row.salaryPass}
              label="Salary"
              detail={
                row.salaryRequired <= 0
                  ? 'No minimum'
                  : row.salaryUnknown
                    ? `Min $${row.salaryRequired.toLocaleString()} -- salary data unavailable`
                    : row.salaryPass
                      ? `$${row.basicSalary.toLocaleString()} (min $${row.salaryRequired.toLocaleString()})`
                      : `$${row.basicSalary.toLocaleString()} -- need $${row.salaryRequired.toLocaleString()}`
              }
            />
            <CriteriaItem
              pass={row.certsUnknown ? null : (row.certsRequired ? row.certsPass : true)}
              label="Skills Assessment"
              detail={row.skillsDetail}
            />
          </div>

          {(onFileChips.length > 0 || (!row.certsPass && !row.certsUnknown && stillNeeded.length > 0)) && (
            <div className="mt-3 space-y-2">
              {onFileChips.length > 0 && (
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-green-700 mb-1">
                    On file
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {onFileChips.slice(0, 4).map((chip) => (
                      <InfoBadge
                        key={chip.key}
                        color="green"
                        label={chip.label}
                        size="xs"
                        className="max-w-[14rem]"
                        title={chip.title}
                      />
                    ))}
                    {onFileChips.length > 4 && (
                      <InfoBadge color="gray" label={`+${onFileChips.length - 4} more`} size="xs" />
                    )}
                  </div>
                </div>
              )}
              {!row.certsPass && !row.certsUnknown && stillNeeded.length > 0 && (
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-amber-700 mb-1">
                    {showAltHint ? 'Still needed — pick one route' : 'Still needed'}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {stillNeeded.slice(0, 4).map((cert) => (
                      <InfoBadge
                        key={`${row.pathway.pathway_name}-${cert.certification_name}`}
                        color="orange"
                        label={cert.certification_short_name || cert.certification_name}
                        size="xs"
                        title={cert.detail || cert.certification_name}
                      />
                    ))}
                    {stillNeeded.length > 4 && (
                      <InfoBadge
                        color="gray"
                        label={`+${stillNeeded.length - 4} more`}
                        size="xs"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {row.allPass && !isRegisteredActive && constructionData?.potential_monthly_levy_savings > 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm text-green-700 bg-green-100 rounded-lg px-3 py-2">
              <DollarSign className="w-4 h-4" />
              <span>
                {isRegisteredLapsed ? 'Renewing via CET restores' : 'Upgrading saves'}{' '}
                <strong>${constructionData.potential_monthly_levy_savings}/mth</strong>
                {constructionData.annual_levy_savings > 0 && (
                  <> (${constructionData.annual_levy_savings}/yr)</>
                )}
              </span>
            </div>
          )}
        </div>
      );
    })}
  </div>
);


const CriteriaItem = ({ pass, label, detail }) => {
  let icon;
  let textColor;
  if (pass === null) {
    icon = <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />;
    textColor = 'text-amber-700';
  } else if (pass) {
    icon = <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
    textColor = 'text-green-700';
  } else {
    icon = <Clock className="w-3.5 h-3.5 text-red-400" />;
    textColor = 'text-red-600';
  }

  return (
    <div className={`flex items-start gap-1.5 ${textColor}`}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div>
        <p className="font-medium text-gray-700">{label}</p>
        <p className="text-xs">{detail}</p>
      </div>
    </div>
  );
};


const MissingCertsList = ({ missingCerts }) => {
  const grouped = (missingCerts || []).reduce((acc, req) => {
    const key = req.pathway_name || 'other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(req);
    return acc;
  }, {});

  const pathwayOrder = ['multi_skilling', 'direct_r1', 'coretrade', 'mbf', 'other'];
  const keys = Object.keys(grouped).sort((a, b) => {
    const ia = pathwayOrder.indexOf(a);
    const ib = pathwayOrder.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  return (
    <div className="space-y-4">
      {keys.map((pathwayKey) => (
        <div key={pathwayKey} className="space-y-2">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            {PATHWAY_LABELS[pathwayKey] || pathwayKey.replace(/_/g, ' ')}
            <span className="ml-1.5 font-normal text-gray-400 normal-case">
              ({grouped[pathwayKey].length})
            </span>
          </p>
          {grouped[pathwayKey].map((req, index) => (
            <div
              key={`${pathwayKey}-${req.certification_name}-${index}`}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100"
            >
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Info className="w-4 h-4 text-amber-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-0.5">
                  <p className="text-sm font-medium text-gray-900">
                    {req.certification_name}
                  </p>
                  {req.is_alternative_route && (
                    <InfoBadge color="blue" label="Route option" size="xs" />
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {req.detail || req.issuing_authority}
                </p>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default SkillsUpgradingTab;
